"use client";

import { useEffect } from "react";
import {
  initializeMetaPixel,
  isMetaPixelConsentGiven,
  setupMetaPixelCookiebotListeners,
} from "@/utils/metaPixelConsent";

const COOKIEBOT_INIT_DELAY = 1000;

/**
 * Meta Pixel Script Component with Cookiebot Consent Management
 * This component handles GDPR-compliant initialization of Meta Pixel
 *
 * ⚠️ IMPORTANT: This component should only be used ONCE per application,
 * typically in your root layout file. Multiple instances could cause
 * duplicate event listeners and tracking issues.
 *
 * Usage:
 * ```tsx
 * // In app/layout.tsx or pages/_app.tsx
 * import { MetaPixelScript } from '@/scripts/MetaPixelScript';
 *
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <MetaPixelScript />
 *         {children}
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 *
 * Note: Meta Pixel will only load if the user has given marketing consent via Cookiebot
 */
export function MetaPixelScript() {
  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_META_PIXEL_ID?.trim()) return;

    // Setup Cookiebot event listeners
    const cleanup = setupMetaPixelCookiebotListeners();

    let timeoutId: NodeJS.Timeout | null = null;

    // Check consent with retry logic to handle Cookiebot loading delays
    const checkConsentWithRetry = () => {
      if (isMetaPixelConsentGiven()) {
        // Consent is given, initialize Meta Pixel
        initializeMetaPixel();
      } else if (typeof window !== "undefined" && !window.Cookiebot) {
        // Cookiebot not loaded yet, retry after delay
        timeoutId = setTimeout(checkConsentWithRetry, COOKIEBOT_INIT_DELAY);
      }
      // If Cookiebot is loaded but consent not given, do nothing
      // (listeners will handle consent changes)
    };

    // Start checking consent
    checkConsentWithRetry();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      cleanup();

      // Note: We intentionally don't remove the Meta Pixel script tag on cleanup
      // because:
      // 1. The script may be used by other parts of the application
      // 2. Removing it could break in-flight tracking requests
      // 3. It's a singleton resource that should persist for the session
      // The event listeners are properly cleaned up above
    };
  }, []);

  // Do not render a Meta <noscript> pixel: it would bypass Cookiebot consent
  // for JavaScript-disabled users and create non-consented marketing requests.
  return null;
}
